/**
 * Platform console — V3 R5, `/platform`.
 *
 * Visible only to the platform owner. Workspace table with storage usage, the
 * quota editor, and the suspend toggle. Metadata only: this screen never shows
 * tenant content, and the API it calls cannot return any.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, MailCheck, Save, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError, api } from '@/lib/api';
import { PlatformSettingsCard } from './PlatformSettings';
import { PlatformCalendarCard } from './PlatformCalendarCard';

interface WorkspaceRow {
  _id: string;
  name: string;
  slug: string;
  status: 'pending_verification' | 'active' | 'suspended';
  plan: {
    name: string;
    storagePerProjectMB: number;
    maxMembers?: number | null;
    allowPlatformChannels?: boolean;
  };
  // V5-07 §4.2 — the founder's cost columns.
  usage?: { storageBytes: number; emailsSent: number; whatsappSent: number };
  quotas?: { storageMB: number; emailsPerMonth: number; whatsappPerMonth: number };
  owner: { name: string; email: string } | null;
  members: number;
  projects: number;
  storageUsedBytes: number;
  createdAt: string;
}

interface Metrics {
  workspaces: number;
  suspended: number;
  pendingVerification: number;
  users: number;
  projects: number;
  storageUsedBytes: number;
}

const mb = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** `used / limit`, coloured once it matters. Amber at 80%, red at the limit. */
function UsageCell({ used, limit, format }: { used: number; limit: number; format?: (n: number) => string }) {
  const percentage = limit > 0 ? Math.round((used / limit) * 100) : 0;
  const show = format ?? ((n: number) => String(n));
  return (
    <span
      className="vt-mono text-[12px]"
      style={{
        color:
          percentage >= 100 ? 'var(--danger)' : percentage >= 80 ? 'var(--warn)' : undefined,
      }}
      title={`${percentage}%`}
    >
      {show(used)} / {show(limit)}
    </span>
  );
}

/**
 * The three plan quotas — V5-07 §4.2.
 *
 * Distinct from `QuotaEditor` below, which sets `storagePerProjectMB`: that
 * caps a single project, these cap the workspace against what the platform
 * actually pays for.
 */
function PlanQuotaEditor({ row }: { row: WorkspaceRow }) {
  const qc = useQueryClient();
  const quotas = row.quotas ?? { storageMB: 1024, emailsPerMonth: 500, whatsappPerMonth: 100 };
  const [draft, setDraft] = useState(quotas);

  const save = useMutation({
    mutationFn: async (next: typeof quotas) =>
      api.patch(`/platform/workspaces/${row._id}/plan`, { quotas: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-workspaces'] });
      toast.success(`${row.name}: quotas updated`);
    },
  });

  const field = (key: keyof typeof quotas, label: string) => (
    <label className="flex items-center gap-1 text-[11px]">
      <span className="vt-muted w-[52px]">{label}</span>
      <Input
        className="h-6 w-20"
        aria-label={`${label} quota for ${row.name}`}
        data-testid={`quota-${key}`}
        value={String(draft[key])}
        inputMode="numeric"
        onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) || 0 })}
      />
    </label>
  );

  return (
    <div className="flex flex-col gap-1">
      {field('storageMB', 'MB')}
      {field('emailsPerMonth', 'Email')}
      {field('whatsappPerMonth', 'WA')}
      <Button
        variant="ghost"
        size="sm"
        data-testid="save-quotas"
        disabled={save.isPending}
        onClick={() => save.mutate(draft)}
      >
        {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
        Save
      </Button>
    </div>
  );
}

function useWorkspaces() {
  return useQuery({
    queryKey: ['platform-workspaces'],
    retry: false,
    queryFn: async () => (await api.get<WorkspaceRow[]>('/platform/workspaces')).data,
  });
}

function useMetrics() {
  return useQuery({
    queryKey: ['platform-metrics'],
    retry: false,
    queryFn: async () => (await api.get<Metrics>('/platform/metrics')).data,
  });
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="vt-surface p-3">
      <div className="text-[11px] uppercase tracking-wide vt-muted">{label}</div>
      <div className="vt-mono text-[24px] font-semibold">{value}</div>
    </div>
  );
}

function QuotaEditor({ row }: { row: WorkspaceRow }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(String(row.plan.storagePerProjectMB));

  const save = useMutation({
    mutationFn: async (storagePerProjectMB: number) =>
      api.patch(`/platform/workspaces/${row._id}/plan`, { storagePerProjectMB, name: 'custom' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-workspaces'] });
      toast.success(`${row.name}: storage limit updated`);
    },
  });

  return (
    <div className="flex items-center gap-1">
      <Input
        aria-label={`Storage limit for ${row.name} in MB`}
        data-testid="quota-input"
        className="h-7 w-24"
        value={value}
        inputMode="numeric"
        onChange={(e) => setValue(e.target.value)}
      />
      <span className="text-[11px] vt-muted">MB</span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Save the limit for ${row.name}`}
        data-testid="save-quota"
        disabled={save.isPending || !Number.isFinite(Number(value))}
        onClick={() => save.mutate(Number(value))}
      >
        {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
      </Button>
    </div>
  );
}

/**
 * Platform channel access — V5-05 §2.1.
 *
 * Defaults on. Switching it off means a workspace with no key of its own sends
 * nothing at all, which is precisely the state that caused the V5-05 outage —
 * so the label says what happens rather than naming the flag.
 */
function ChannelsToggle({ row }: { row: WorkspaceRow }) {
  const qc = useQueryClient();
  const allowed = row.plan.allowPlatformChannels !== false;

  const save = useMutation({
    mutationFn: async (next: boolean) =>
      api.patch(`/platform/workspaces/${row._id}/plan`, { allowPlatformChannels: next }),
    onSuccess: (_data, next) => {
      void qc.invalidateQueries({ queryKey: ['platform-workspaces'] });
      toast.success(
        next
          ? `${row.name}: may now send on the platform's keys`
          : `${row.name}: can now only send via its own keys`,
      );
    },
  });

  return (
    <label className="flex items-start gap-2 text-[12px]" title={CHANNELS_LABEL}>
      <input
        type="checkbox"
        data-testid="platform-channels-toggle"
        aria-label={`${CHANNELS_LABEL} — ${row.name}`}
        checked={allowed}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.checked)}
      />
      <span className={allowed ? '' : 'vt-muted'}>{allowed ? 'On' : 'Own keys only'}</span>
    </label>
  );
}

const CHANNELS_LABEL =
  'Platform email/WhatsApp channels — when off, this workspace can only send via its own keys';

/**
 * Last 50 delivery attempts for one workspace — V5-05 §2.4.
 *
 * The panel the founder did not have: it answers "was an email even attempted,
 * and what happened to it" without a shell on the production box.
 */
function DeliveriesPanel({ row }: { row: WorkspaceRow }) {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-deliveries', row._id],
    retry: false,
    queryFn: async () =>
      (
        await api.get<
          {
            _id: string;
            channel: string;
            template: string | null;
            recipient: string | null;
            status: string;
            skipReason: string | null;
            providerMessageId: string | null;
            errorMessage: string | null;
            createdAt: string;
          }[]
        >(`/platform/workspaces/${row._id}/deliveries`)
      ).data,
  });

  if (isLoading) return <Skeleton className="h-24" />;
  if (!data?.length) {
    return (
      <p className="vt-muted p-3 text-[12px]" data-testid="deliveries-empty">
        No delivery attempts recorded for {row.name} yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto p-2" data-testid="deliveries-panel">
      <table className="w-full text-left text-[12px]">
        <thead className="vt-muted">
          <tr>
            <th className="px-2 py-1 font-medium">When</th>
            <th className="px-2 py-1 font-medium">Channel</th>
            <th className="px-2 py-1 font-medium">For</th>
            <th className="px-2 py-1 font-medium">To</th>
            <th className="px-2 py-1 font-medium">Result</th>
            <th className="px-2 py-1 font-medium">Provider id</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d._id} className="border-t border-[var(--border)]" data-testid="delivery-row">
              <td className="vt-mono whitespace-nowrap px-2 py-1">
                {new Date(d.createdAt).toLocaleString()}
              </td>
              <td className="px-2 py-1">{d.channel}</td>
              <td className="vt-mono px-2 py-1">{d.template ?? '—'}</td>
              <td className="px-2 py-1">{d.recipient ?? '—'}</td>
              <td className="px-2 py-1">
                <Badge
                  tone={d.status === 'sent' ? 'accent' : d.status === 'failed' ? 'danger' : 'warn'}
                >
                  {d.status}
                </Badge>
                {d.skipReason && (
                  <span className="vt-muted ml-1" data-testid="delivery-skip-reason">
                    {d.skipReason}
                  </span>
                )}
                {d.errorMessage && <span className="ml-1 text-[var(--danger)]">{d.errorMessage}</span>}
              </td>
              <td className="vt-mono px-2 py-1">{d.providerMessageId ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Six months of usage against the plan — V5-07 §4.2.
 *
 * Six because that is enough to see a trend without turning the console into a
 * reporting tool. Counters are never reset, so a month that is missing is a
 * month with no sends rather than a gap in the data — the empty state says so,
 * because "no row" and "no usage" look identical otherwise.
 */
function UsageHistoryPanel({ row }: { row: WorkspaceRow }) {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-usage-history', row._id],
    retry: false,
    queryFn: async () =>
      (
        await api.get<{
          quotas: { storageMB: number; emailsPerMonth: number; whatsappPerMonth: number };
          storageBytes: number;
          reconciledAt: string | null;
          months: {
            period: string;
            emailsSent: number;
            whatsappSent: number;
            thresholdsNotified: string[];
          }[];
        }>(`/platform/workspaces/${row._id}/usage`)
      ).data,
  });

  if (isLoading) return <Skeleton className="h-24" />;
  if (!data) return null;

  const pct = (used: number, limit: number): number =>
    limit > 0 ? Math.round((used / limit) * 100) : 0;
  const tone = (percentage: number): string | undefined =>
    percentage >= 100 ? 'var(--danger)' : percentage >= 80 ? 'var(--warn)' : undefined;

  return (
    <div className="flex flex-col gap-2 p-3" data-testid="usage-history-panel">
      <div className="flex flex-wrap items-baseline gap-3 text-[12px]">
        <span className="font-medium">Storage now</span>
        <span className="vt-mono" style={{ color: tone(pct(data.storageBytes, data.quotas.storageMB * 1024 * 1024)) }}>
          {mb(data.storageBytes)} / {data.quotas.storageMB} MB
        </span>
        <span className="vt-muted">
          {data.reconciledAt
            ? `reconciled ${new Date(data.reconciledAt).toLocaleString()}`
            : 'not yet reconciled'}
        </span>
      </div>

      {data.months.length === 0 ? (
        <p className="vt-muted text-[12px]" data-testid="usage-history-empty">
          No months recorded yet — this workspace has sent nothing on the platform&rsquo;s keys.
        </p>
      ) : (
        <table className="w-full text-left text-[12px]" data-testid="usage-history-table">
          <thead className="vt-muted">
            <tr>
              <th className="px-2 py-1 font-medium">Month</th>
              <th className="px-2 py-1 font-medium">Emails</th>
              <th className="px-2 py-1 font-medium">WhatsApp</th>
              <th className="px-2 py-1 font-medium">80% warnings sent</th>
            </tr>
          </thead>
          <tbody>
            {data.months.map((month) => (
              <tr
                key={month.period}
                className="border-t border-[var(--border)]"
                data-testid="usage-history-row"
                data-period={month.period}
              >
                <td className="vt-mono px-2 py-1">{month.period}</td>
                <td
                  className="vt-mono px-2 py-1"
                  style={{ color: tone(pct(month.emailsSent, data.quotas.emailsPerMonth)) }}
                >
                  {month.emailsSent} / {data.quotas.emailsPerMonth}
                </td>
                <td
                  className="vt-mono px-2 py-1"
                  style={{ color: tone(pct(month.whatsappSent, data.quotas.whatsappPerMonth)) }}
                >
                  {month.whatsappSent} / {data.quotas.whatsappPerMonth}
                </td>
                <td className="px-2 py-1 vt-muted">
                  {month.thresholdsNotified.length > 0
                    ? month.thresholdsNotified.join(', ')
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Create a company from the console — V4 F-4.
 *
 * The owner never gets a password from here: they are invited and choose their
 * own. An administrator who sets someone else's password is an administrator
 * who knows it.
 */
function CreateWorkspaceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [companyName, setCompanyName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [storage, setStorage] = useState('1024');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ slug: string; ownerEmail: string }>('/platform/workspaces', {
          companyName: companyName.trim(),
          ownerName: ownerName.trim(),
          ownerEmail: ownerEmail.trim(),
          storagePerProjectMB: Number(storage),
        })
      ).data,
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['platform-workspaces'] });
      void qc.invalidateQueries({ queryKey: ['platform-metrics'] });
      toast.success(`${companyName} created — invitation sent to ${result.ownerEmail}`);
      setCompanyName('');
      setOwnerName('');
      setOwnerEmail('');
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'Could not create the workspace'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="mb-1 text-[16px] font-semibold">New workspace</DialogTitle>
        <p className="mb-3 text-[12px] vt-muted">
          The owner is invited by email and sets their own password. Nothing is created for them
          beyond a demo project they can delete.
        </p>

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-company">Company name</Label>
            <Input
              id="ws-company"
              data-testid="ws-company"
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-owner-name">Owner name</Label>
            <Input
              id="ws-owner-name"
              data-testid="ws-owner-name"
              required
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-owner-email">Owner email</Label>
            <Input
              id="ws-owner-email"
              data-testid="ws-owner-email"
              type="email"
              required
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-storage">Storage per project (MB)</Label>
            <Input
              id="ws-storage"
              data-testid="ws-storage"
              inputMode="numeric"
              value={storage}
              onChange={(e) => setStorage(e.target.value)}
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
            >
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              data-testid="create-workspace-submit"
              disabled={create.isPending}
            >
              {create.isPending ? <Loader2 className="animate-spin" /> : <Building2 />}
              Create and invite
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PlatformPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [openDeliveries, setOpenDeliveries] = useState<string | null>(null);
  const [openUsage, setOpenUsage] = useState<string | null>(null);
  const { data: rows, isLoading, error } = useWorkspaces();
  const { data: metrics } = useMetrics();
  const qc = useQueryClient();

  const suspend = useMutation({
    mutationFn: async ({ id, suspended }: { id: string; suspended: boolean }) =>
      api.post(`/platform/workspaces/${id}/suspend`, { suspended }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['platform-workspaces'] });
      void qc.invalidateQueries({ queryKey: ['platform-metrics'] });
      toast.success(variables.suspended ? 'Workspace suspended' : 'Workspace restored');
    },
  });

  const resend = useMutation({
    mutationFn: async (id: string) => api.post(`/platform/workspaces/${id}/resend-verification`),
    onSuccess: () => toast.success('Verification email resent'),
  });

  if (error instanceof ApiError) {
    return (
      <>
        <PageHeader title="Platform" />
        <div className="p-4">
          <EmptyState
            icon={<ShieldOff className="size-6" />}
            title="Not your console"
            description="This area is restricted to the platform administrator."
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Platform"
        subtitle="Every workspace on this installation. Metadata only — never tenant content."
        actions={
          <Button variant="primary" size="sm" data-testid="new-workspace" onClick={() => setCreateOpen(true)}>
            <Building2 />
            New workspace
          </Button>
        }
      />

      <CreateWorkspaceDialog open={createOpen} onOpenChange={setCreateOpen} />

      <div className="flex flex-col gap-4 px-4 pt-4">
        <PlatformSettingsCard />
        <PlatformCalendarCard />
      </div>

      <div className="flex flex-col gap-4 p-4" data-testid="platform-console">
        {metrics && (
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Stat label="Workspaces" value={metrics.workspaces} />
            <Stat label="Pending" value={metrics.pendingVerification} />
            <Stat label="Suspended" value={metrics.suspended} />
            <Stat label="People" value={metrics.users} />
            <Stat label="Storage" value={mb(metrics.storageUsedBytes)} />
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-48" />
        ) : (rows ?? []).length === 0 ? (
          <EmptyState
            icon={<Building2 className="size-6" />}
            title="No workspaces"
            description="Nothing has signed up yet."
          />
        ) : (
          <div className="vt-surface overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-[var(--border)] text-left">
                  <th scope="col" className="px-3 py-2 font-medium">
                    Workspace
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Owner
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    People
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Projects
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Storage
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Emails / mo
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Quotas
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Limit / project
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium" title={CHANNELS_LABEL}>
                    Platform channels
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Deliveries
                  </th>
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).map((row) => (
                  <tr
                    key={row._id}
                    className="border-b border-[var(--border)] last:border-0"
                    data-testid="workspace-row"
                    data-slug={row.slug}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.name}</div>
                      <div className="vt-mono text-[11px] vt-muted">{row.slug}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="truncate">{row.owner?.name ?? '—'}</div>
                      <div className="truncate text-[11px] vt-muted">{row.owner?.email}</div>
                    </td>
                    <td className="vt-mono px-3 py-2">{row.members}</td>
                    <td className="vt-mono px-3 py-2">{row.projects}</td>
                    <td className="px-3 py-2" data-testid="cell-storage">
                      <UsageCell
                        used={row.usage?.storageBytes ?? row.storageUsedBytes}
                        limit={(row.quotas?.storageMB ?? 1024) * 1024 * 1024}
                        format={mb}
                      />
                    </td>
                    <td className="px-3 py-2" data-testid="cell-emails">
                      <UsageCell
                        used={row.usage?.emailsSent ?? 0}
                        limit={row.quotas?.emailsPerMonth ?? 500}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <QuotaEditor row={row} />
                    </td>
                    <td className="px-3 py-2">
                      <PlanQuotaEditor row={row} />
                    </td>
                    <td className="px-3 py-2">
                      <ChannelsToggle row={row} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <Badge
                          tone={
                            row.status === 'active'
                              ? 'accent'
                              : row.status === 'suspended'
                                ? 'danger'
                                : 'warn'
                          }
                        >
                          {row.status.replace('_', ' ')}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="suspend-toggle"
                          onClick={() =>
                            suspend.mutate({ id: row._id, suspended: row.status !== 'suspended' })
                          }
                        >
                          {row.status === 'suspended' ? 'Restore' : 'Suspend'}
                        </Button>
                        {row.status === 'pending_verification' && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Resend verification for ${row.name}`}
                            onClick={() => resend.mutate(row._id)}
                          >
                            <MailCheck />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="toggle-deliveries"
                          aria-expanded={openDeliveries === row._id}
                          onClick={() =>
                            setOpenDeliveries(openDeliveries === row._id ? null : row._id)
                          }
                        >
                          {openDeliveries === row._id ? 'Hide' : 'Deliveries'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="toggle-usage-history"
                          aria-expanded={openUsage === row._id}
                          onClick={() => setOpenUsage(openUsage === row._id ? null : row._id)}
                        >
                          {openUsage === row._id ? 'Hide' : 'Usage'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Rendered as its own full-width row so the table keeps its
                    column alignment when a panel is open. */}
                {(rows ?? [])
                  .filter((row) => row._id === openDeliveries)
                  .map((row) => (
                    <tr key={`${row._id}-deliveries`} data-testid="deliveries-row">
                      <td colSpan={11} className="bg-[var(--surface-2)] p-0">
                        <DeliveriesPanel row={row} />
                      </td>
                    </tr>
                  ))}
                {(rows ?? [])
                  .filter((row) => row._id === openUsage)
                  .map((row) => (
                    <tr key={`${row._id}-usage`} data-testid="usage-history-row-container">
                      <td colSpan={11} className="bg-[var(--surface-2)] p-0">
                        <UsageHistoryPanel row={row} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
